import type { ChipProps, SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import type { WaitingRoomQueueState } from './types';

export const WAITING_ROOM_ACTIVE_STATES: readonly WaitingRoomQueueState[] = [
  'CHECKED_IN',
  'DEVICE_CHECK',
  'IN_QUEUE',
  'ADMITTED',
  'IN_CALL',
];

const WAITING_ROOM_STATE_LABELS: Record<WaitingRoomQueueState, string> = {
  CHECKED_IN: 'Checked in',
  DEVICE_CHECK: 'Device check',
  IN_QUEUE: 'In queue',
  ADMITTED: 'Admitted',
  IN_CALL: 'In call',
  COMPLETED: 'Completed',
  REMOVED: 'Removed',
  ABANDONED: 'Left waiting room',
};

const WAITING_ROOM_STATE_COLORS: Record<WaitingRoomQueueState, ChipProps['color']> = {
  CHECKED_IN: 'info',
  DEVICE_CHECK: 'warning',
  IN_QUEUE: 'primary',
  ADMITTED: 'success',
  IN_CALL: 'success',
  COMPLETED: 'default',
  REMOVED: 'error',
  ABANDONED: 'default',
};

export function getWaitingRoomStateLabel(state: WaitingRoomQueueState): string {
  return WAITING_ROOM_STATE_LABELS[state] ?? state;
}

export function getWaitingRoomStateColor(state: WaitingRoomQueueState): ChipProps['color'] {
  return WAITING_ROOM_STATE_COLORS[state] ?? 'default';
}

export function getWaitingRoomWaitColor(waitSeconds?: number | null): ChipProps['color'] {
  if (waitSeconds == null) {
    return 'default';
  }
  if (waitSeconds < 5 * 60) {
    return 'success';
  }
  if (waitSeconds <= 10 * 60) {
    return 'warning';
  }
  return 'error';
}

export function formatWaitingRoomWait(waitSeconds?: number | null): string {
  if (waitSeconds == null) {
    return 'Waiting';
  }

  if (waitSeconds < 60) {
    return 'Under 1 min';
  }

  const minutes = Math.floor(waitSeconds / 60);
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${remainingMinutes} min`;
}

export function getWaitingRoomInitials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return '?';
  }

  return parts
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
}

export function getWaitingRoomDarkChipSx(color: ChipProps['color'] = 'default'): SxProps<Theme> {
  return theme => {
    if (!color || color === 'default') {
      return {
        bgcolor: alpha(theme.palette.common.white, 0.14),
        color: theme.palette.grey[100],
      };
    }

    const paletteColor = theme.palette[color];
    if (!paletteColor || typeof paletteColor !== 'object' || !('main' in paletteColor)) {
      return {
        bgcolor: alpha(theme.palette.common.white, 0.14),
        color: theme.palette.grey[100],
      };
    }

    return {
      bgcolor: alpha(paletteColor.main, 0.22),
      color: paletteColor.light ?? theme.palette.common.white,
      fontWeight: 700,
    };
  };
}
